import React, { useCallback } from 'react'
import { Link, useParams } from 'react-router-dom'
import { API } from '@Root/API'
import { HandbookTable } from '../HandbookTable'
import { useHandbookApi } from '../useHandbookApi'
import { Callout, ErrorState, LoadingState, Section, Steps } from '../ui'
import styles from '../style.module.scss'

const fetchReference = () => API.handbook.getReference()

export const ReferenceIndex = () => {
  const { data, loading, error } = useHandbookApi(fetchReference)

  if (loading) {
    return <LoadingState />
  }
  if (error) {
    return <ErrorState>Unable to load reference tables.</ErrorState>
  }

  return (
    <>
      <div className={styles.pageHead}>
        <p className={styles.kicker}>Lookups</p>
        <p className={styles.lead}>HESA and lookup code lists shared by Cognito mapping and the admissions UI.</p>
      </div>
      <Callout>
        Send either the numeric TEID id (for example <code>206</code>), mapped to the <code>*_id</code> field with the integer transform, or a clear label (for example <code>Ukraine</code>) that matches the search field, mapped to the <code>*_title</code> field for lookup.
      </Callout>
      <Section title='Tables'>
        <HandbookTable
          columns={[
            { key: 'title', label: 'Reference', render: (value, row) => <Link to={`/home/handbook/reference/${row.slug}`}>{value}</Link> },
            { key: 'table', label: 'DB table', render: value => <code>{value}</code> },
            { key: 'search_field', label: 'Search field', render: value => <code>{value}</code> },
          ]}
          rows={data?.tables || []}
        />
      </Section>
    </>
  )
}

export const ReferenceTablePage = () => {
  const { table } = useParams()
  const fetchTable = useCallback(() => API.handbook.getReferenceTable(table), [table])
  const { data, loading, error } = useHandbookApi(fetchTable)

  if (loading) {
    return <LoadingState />
  }
  if (error) {
    return <ErrorState>Unable to load this reference table.</ErrorState>
  }

  if (data?.type === 'guide') {
    return (
      <>
        <div className={styles.crumb}>
          <Link to='/home/handbook/reference'>Reference</Link>
          <span className={styles.crumbSep}>/</span>
          <span>{data.title}</span>
        </div>
        <div className={styles.pageHead}>
          <p className={styles.kicker}>Lookup</p>
          <p className={styles.lead}>{data.guide?.lead}</p>
        </div>
        <Section title='Resolution'>
          <Steps items={data.guide?.steps || []} />
        </Section>
        <Callout>{data.guide?.example}</Callout>
      </>
    )
  }

  if (data?.type === 'static') {
    return (
      <>
        <div className={styles.crumb}>
          <Link to='/home/handbook/reference'>Reference</Link>
          <span className={styles.crumbSep}>/</span>
          <span>{data.title}</span>
        </div>
        <div className={styles.pageHead}>
          <p className={styles.kicker}>Option codes</p>
          <p className={styles.lead}>{data.lead}</p>
        </div>
        {(data.sections || []).map(section => (
          <Section key={section.id} title={section.title} hint={section.lead}>
            <div id={section.id}>
              <HandbookTable
                columns={(section.columns || []).map((label, index) => ({
                  key: String(index),
                  label,
                  render: value => (index === 0 ? <code>{value}</code> : value),
                }))}
                rows={(section.rows || []).map(row => {
                  const item = {}
                  ;(section.columns || []).forEach((_, index) => {
                    item[String(index)] = row[index]
                  })
                  return item
                })}
              />
            </div>
          </Section>
        ))}
      </>
    )
  }

  const columns = (data?.columns || []).map(column => ({
    key: column,
    label: column === data.label_col ? 'Label' : column.replace('_', ' '),
    render: value => (column === 'id' || column === data.label_col ? String(value ?? '') : value),
  }))

  return (
    <>
      <div className={styles.crumb}>
        <Link to='/home/handbook/reference'>Reference</Link>
        <span className={styles.crumbSep}>/</span>
        <span>{data?.title}</span>
      </div>
      <div className={styles.pageHead}>
        <p className={styles.kicker}>{data?.table}</p>
        <p className={styles.lead}>
          Search field <code>{data?.search_field}</code>
        </p>
      </div>
      {data?.lead && <Callout>{data.lead}</Callout>}
      <Section title='Codes' hint='Send the numeric TEID id, mapped to the *_id field with the integer transform, or a clear label that matches the search field, mapped to the *_title field for lookup.'>
        <HandbookTable filter columns={columns} rows={data?.rows || []} />
      </Section>
    </>
  )
}
